'use client'

import { SiYoutube } from '@icons-pack/react-simple-icons'
import { Dithering } from '@paper-design/shaders-react'
import { motion, useMotionValue, useSpring } from 'framer-motion'
import { ArrowUpRightIcon, ClockIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '~/components/ui/avatar'
import { Badge } from '~/components/ui/catalyst/badge'
import { Button } from '~/components/ui/catalyst/button'
import { AvatarStack } from '~/components/ui/kibo-ui/avatar-stack'
import { cn, getCdnPath } from '~/lib/utils'
import { LinkWrapper } from './feature-card'

/**
 * Detects low-end devices based on hardware capabilities
 * Returns true for devices with <= 4 CPU cores or <= 4GB memory
 */
function useIsLowEndDevice(): boolean {
  const [isLowEnd, setIsLowEnd] = useState(false)

  useEffect(() => {
    const cores = navigator.hardwareConcurrency ?? 4
    // deviceMemory is in GB, available in Chrome/Edge
    const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 8

    // Consider low-end if 4 or fewer cores, or 4GB or less memory
    const isLowEndDevice = cores <= 4 || memory <= 4
    setIsLowEnd(isLowEndDevice)
  }, [])

  return isLowEnd
}

interface QuestionsBaseCardProps {
  className?: string
}

export function QuestionsBaseCard(props: QuestionsBaseCardProps) {
  const isLowEndDevice = useIsLowEndDevice()

  const speedMotionValue = useMotionValue(0)
  const springSpeed = useSpring(speedMotionValue, {
    stiffness: 200,
    damping: 25,
    mass: 1,
  })

  const [currentSpeed, setCurrentSpeed] = useState(0)

  useEffect(() => {
    // On low-end devices, keep speed at 0 (static)
    if (isLowEndDevice) {
      setCurrentSpeed(0)
      return
    }

    // Set initial speed for normal devices
    speedMotionValue.set(0.1)

    const unsubscribe = springSpeed.on('change', (value) => {
      setCurrentSpeed(value)
    })
    return unsubscribe
  }, [springSpeed, isLowEndDevice, speedMotionValue])

  const handleMouseEnter = () => {
    if (!isLowEndDevice) {
      speedMotionValue.set(1.5)
    }
  }

  const handleMouseLeave = () => {
    if (!isLowEndDevice) {
      speedMotionValue.set(0.1)
    }
  }

  return (
    <LinkWrapper
      href="/questions"
      className={cn(
        'group flex rounded-[calc(var(--radius)+var(--padding))] [--padding:12px] [--radius:8px]',
        props.className,
      )}
    >
      <section
        className={`
          flex w-full flex-col gap-2 rounded-[calc(var(--radius)+var(--padding))] bg-neutral-100 p-(--padding)
          dark:bg-gray-900
        `}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <motion.div
          className="relative min-h-[280px] w-full overflow-hidden rounded-(--radius) border shadow-xs"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className={`
            absolute z-2 flex size-full transform-gpu items-end justify-start bg-gradient-to-t from-white to-transparent
          `}
          >
            <div className="space-y-1 rounded-(--radius) p-4">
              <h3 className="flex items-center gap-2 text-2xl/6 font-semibold">
                <span>База вопросов</span>
                <span className="mt-0.5 inline-flex size-fit rounded-md bg-white">
                  <Badge color="sky">Закрытая бета</Badge>
                </span>
              </h3>
              <p className="max-w-4xl text-lg/6 font-medium text-pretty text-muted-foreground">
                Актуальные вопросы, собранные с реальных собеседований по разным направлениям
              </p>
            </div>
          </div>

          <div
            className="absolute inset-0 z-0 size-full"
          >
            <Dithering
              className="size-full"
              colorBack="#fafafa"
              colorFront="#fa7319"
              shape="swirl"
              type="8x8"
              pxSize={3}
              offsetX={0}
              offsetY={0}
              scale={1}
              rotation={2}
              speed={currentSpeed}
            />
          </div>
        </motion.div>

        <motion.div
          className="hidden gap-2 sm:flex"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div
            className="flex w-full flex-col gap-3 rounded-(--radius) bg-white p-3 dark:bg-gray-800"
          >
            <span className="text-xs font-medium">Вопросы из топовых компаний</span>

            <AvatarStack>
              <Avatar>
                <AvatarImage src={getCdnPath('/assets/companies/yandex.png')} />
                <AvatarFallback>YA</AvatarFallback>
              </Avatar>
              <Avatar>
                <AvatarImage src={getCdnPath('/assets/companies/sber.png')} />
                <AvatarFallback>SB</AvatarFallback>
              </Avatar>
              <Avatar>
                <AvatarImage src={getCdnPath('/assets/companies/wb.jpg')} />
                <AvatarFallback>WB</AvatarFallback>
              </Avatar>
              <Avatar>
                <AvatarImage src={getCdnPath('/assets/companies/ozon.jpeg')} />
                <AvatarFallback>OZ</AvatarFallback>
              </Avatar>
              <Avatar>
                <AvatarImage src={getCdnPath('/assets/companies/avito.jpg')} />
                <AvatarFallback>AV</AvatarFallback>
              </Avatar>
              <Avatar>
                <AvatarImage />
                <AvatarFallback>91</AvatarFallback>
              </Avatar>
            </AvatarStack>
          </div>

          <div
            className={`
              relative flex w-full flex-col gap-3 overflow-hidden rounded-(--radius) bg-white p-3
              dark:bg-gray-800
            `}
          >
            <SiYoutube className={`
              pointer-events-none absolute -top-4 -right-2 z-0 size-24 scale-110 rotate-12 transform text-red-500/10
            `}
            />
            <span className="relative z-1 text-xs font-medium">Видео-ответы</span>
            <p className="relative z-1 text-sm text-pretty text-gray-600 dark:text-gray-300">
              На большинство вопросов можно посмотреть видео с ответами реальных разработчиков
            </p>
          </div>

          <div
            className={`
              relative hidden w-full flex-col gap-3 overflow-hidden rounded-(--radius) bg-white p-3
              md:flex
              dark:bg-gray-800
            `}
          >
            <ClockIcon className={`
              pointer-events-none absolute -top-3 -right-3 z-0 size-20 scale-125 -rotate-6 transform text-green-500/10
            `}
            />
            <span className="relative z-1 text-xs font-medium">Актуальная база</span>
            <p className="relative z-1 text-sm text-gray-600 dark:text-gray-300">
              Коллекция вопросов пополняется новыми материалами каждую неделю
            </p>
          </div>
        </motion.div>

        <div>
          <Button color="orange" className="w-full rounded-(--radius)">
            <span>Перейти в раздел</span>
            <ArrowUpRightIcon className={`
              size-5 translate-y-1 transition-transform duration-200 ease-in-out
              group-hover:translate-x-0.5 group-hover:translate-y-0.5
            `}
            />
          </Button>
        </div>
      </section>
    </LinkWrapper>
  )
}
